import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Button, Spinner, Alert } from 'react-bootstrap';
import { selectAdminUuid } from '../../features/auth/authSlice';
import { getRoleJSON } from '../utils';
import { useListEntrepreneurApplicationsQuery, useSetFirmStageMutation } from '../../app/api';
import {
  brand, cardStyle, headStyle, EntPage, ClaudeTrackBadge, claudeSignedUp,
  ActionItem, ActionsCard, Fact, StageBadge,
} from './common';
import FirmUsersSection from './FirmUsersSection';

/* One firm's HUB page (Entrepreneur -> Firms -> a firm): the people who log in
 * to the firm's workspace, which is what every firm has.
 *
 * Claude is deliberately not run from here. The application, domain, seat
 * queue and billing live on the firm's Claude page (ClaudeFirmDetail, under
 * Entrepreneur -> Claude -> Claude Firms), and this page only carries a small
 * card linking across. The one Claude thing kept on this page is each member's
 * seat state, because asking for a seat is a decision about a person. */

export default function FirmDetail() {
  const { firmId: firmIdParam } = useParams();
  const firmId = Number(firmIdParam);
  const navigate = useNavigate();

  const { data: apps = [], isLoading, refetch: refetchApps } = useListEntrepreneurApplicationsQuery();

  // "Log in as" is a Prosperity-only support tool, restricted to employees who
  // hold BOTH Admin and Entrepreneur (the /entrepreneur section already requires
  // ENTREPRENEUR, so this adds the ADMIN check). The button is hidden otherwise;
  // the backend enforces the same gate regardless.
  const adminUuid = useSelector(selectAdminUuid);
  const [canImpersonate, setCanImpersonate] = useState(false);
  useEffect(() => {
    if (!adminUuid) return undefined;
    let cancelled = false;
    getRoleJSON(adminUuid)
      .then((rj) => { if (!cancelled) setCanImpersonate(Boolean(rj?.isAdmin && rj?.isEntrepreneur)); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [adminUuid]);

  const app = useMemo(() => apps.find(a => a.firm_id === firmId), [apps, firmId]);

  const [setFirmStage] = useSetFirmStageMutation();
  const [stageBusy, setStageBusy] = useState(false);
  const [stageError, setStageError] = useState(null);
  const changeStage = async (stage) => {
    const question = stage === 'active'
      ? `Take ${app.firm_name} live? Everyone at the firm moves from the transition dashboard to the full workspace on their next page load.`
      : `Mark ${app.firm_name} as transitioning? Everyone at the firm will see only the transition dashboard until you take it live.`;
    if (!window.confirm(question)) return;
    setStageBusy(true); setStageError(null);
    try {
      await setFirmStage({ firm_id: firmId, stage }).unwrap();
      await refetchApps();
    } catch (err) {
      setStageError(err?.data?.detail || 'Could not change the firm\'s stage.');
    }
    setStageBusy(false);
  };

  const backButton = (
    <Button variant="outline-light" size="sm" onClick={() => navigate('/entrepreneur/firms')}>
      <i className="bi bi-arrow-left me-1" />All firms
    </Button>
  );

  if (isLoading) {
    return <EntPage title="Firm" actions={backButton}><div className="text-center p-5"><Spinner /></div></EntPage>;
  }
  if (!app) {
    return (
      <EntPage title="Firm not found" actions={backButton}>
        <Card style={cardStyle}><Card.Body style={{ color: brand.charcoal }}>This firm doesn&apos;t exist or has been removed.</Card.Body></Card>
      </EntPage>
    );
  }

  const memberCount = app.member_count ?? app.active_seats ?? 0;
  const adminCount = app.admin_count ?? 0;
  const awaiting = app.awaiting_first_login ?? 0;
  const signedUp = claudeSignedUp(app);
  const hasClaude = signedUp || (app.claude_seats || 0) > 0 || (app.pending_seats || 0) > 0;

  // ── Outstanding hub actions ──
  const actions = [];
  if (memberCount === 0) {
    actions.push(
      <ActionItem key="add" icon="person-plus" title="Add the firm's first member"
        detail="Adding someone sends their hub login invite. Make at least one person an Admin so the firm can invite the rest of its team." />,
    );
  } else if (adminCount === 0) {
    actions.push(
      <ActionItem key="admin" icon="person-gear" title="No firm admin yet"
        detail="Nobody at this firm can invite their own team or manage members. Set someone's role to Admin below." />,
    );
  }

  const transitioning = app.stage === 'transitioning';

  return (
    <EntPage
      title={app.firm_name}
      subtitle={`${memberCount} member${memberCount === 1 ? '' : 's'}`}
      actions={backButton}
    >
      <ActionsCard actions={actions} doneText="Nothing outstanding for this firm." />

      {/* Transitioning firms: the whole firm sees only its transition dashboard
          until it goes live. Live firms just get a quiet way in. */}
      {stageError && <Alert variant="danger" className="py-2">{stageError}</Alert>}
      {transitioning ? (
        <Card style={{ ...cardStyle, marginBottom: '1.25rem', borderLeft: `6px solid ${brand.goldenrod}` }}>
          <Card.Body style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 260 }}>
              <div style={{ fontWeight: 700, color: brand.deepBlue }}>
                Transitioning <StageBadge stage={app.stage} style={{ marginLeft: 6 }} />
              </div>
              <div style={{ color: brand.charcoal, fontSize: '0.85rem' }}>
                Everyone at this firm sees only their transition dashboard - no client data, documents or Claude.
                Taking the firm live gives each person the access their role below says, straight away.
              </div>
            </div>
            <Button size="sm" style={{ background: brand.deepBlue, border: 'none' }} onClick={() => navigate(`/entrepreneur/transitions/${firmId}`)}>
              <i className="bi bi-pencil-square me-1" />Edit transition dashboard
            </Button>
            <Button size="sm" variant="outline-success" disabled={stageBusy} onClick={() => changeStage('active')}>
              <i className="bi bi-rocket-takeoff me-1" />Go live
            </Button>
          </Card.Body>
        </Card>
      ) : (
        <div style={{ textAlign: 'right', marginTop: '-0.5rem', marginBottom: '0.5rem' }}>
          <Button variant="link" size="sm" disabled={stageBusy} onClick={() => changeStage('transitioning')}
            style={{ fontSize: '0.78rem', color: brand.charcoal }}>
            Mark as transitioning
          </Button>
        </div>
      )}

      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '1.25rem' }}>
        <Fact label="Members" value={memberCount} />
        <Fact label="Firm admins" value={adminCount} />
        <Fact label="Awaiting first login" value={awaiting} />
        {/* The one link across to Claude. Kept to a single tile so the hub page
            never turns back into a Claude page. */}
        <div style={{ background: 'white', borderRadius: 10, boxShadow: '0 2px 12px rgba(10,41,71,0.08)', padding: '0.6rem 1rem', minWidth: 220 }}>
          <div style={{ color: brand.charcoal, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 700 }}>Claude</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
            <ClaudeTrackBadge status={app.application_status} />
            {hasClaude && (
              <Button variant="link" size="sm" onClick={() => navigate(`/entrepreneur/claude-firms/${firmId}`)} style={{ padding: 0, fontSize: '0.8rem' }}>
                Open Claude page<i className="bi bi-arrow-right ms-1" />
              </Button>
            )}
          </div>
        </div>
      </div>

      <Card style={cardStyle}>
        <Card.Header style={headStyle}>Members</Card.Header>
        <Card.Body>
          <FirmUsersSection firmId={firmId} firmName={app.firm_name} onChange={refetchApps} canImpersonate={canImpersonate} claudeSignedUp={signedUp} transitioning={transitioning} />
        </Card.Body>
      </Card>
    </EntPage>
  );
}
